import React from "react";
import { mdiArrowUp, mdiArrowDown } from "@mdi/js";
import { cn, incPanelColorLevel, panelBackgroundStyles } from "../../../styles";
import { Icon } from "../../../atoms/Icon";
import { useDataTableContext } from "../context";
import { DataTableColumn } from "../types";
import { SortDirection } from "../hooks/useSorting";
import { Typography } from "../../../atoms";
import { frozenCellStyle, frozenCellClassName } from "./frozenStyle";

export interface HeaderCellProps<TRow> {
  column: DataTableColumn<TRow>;
  className?: string;
}

export function HeaderCell<TRow>({ column, className }: HeaderCellProps<TRow>) {
  const { dense, sorting, color, frozen } = useDataTableContext<TRow>();

  const headerColor = incPanelColorLevel(color, 1);
  const freezeInfo = frozen.getFreezeInfo(column.id);

  const isSorted = sorting?.sort.id === column.id;
  const sortDir = isSorted ? sorting?.sort.dir : null;
  const canSort = column.sortable && sorting;

  const handleClick = () => {
    if (canSort) {
      sorting.toggle(column.id);
    }
  };

  const alignClass = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  }[column.align];

  // Sticky offsets are resolved against the scrollport edge, not the th's own
  // padding box — add the header's horizontal padding back in so the label
  // doesn't jump sideways the moment it becomes stuck.
  const paddingX = dense ? 6 : 12;

  return (
    <th
      ref={frozen.getCellRef(column.id)}
      className={cn(
        "border-b border-back-200 dark:border-back-700 font-medium",
        dense ? "px-1.5 py-2 text-xs" : "px-3 py-1.5 text-sm",
        alignClass,
        canSort &&
          "cursor-pointer select-none hover:bg-back-200 dark:hover:bg-back-700",
        column.fitContent && "w-0 whitespace-nowrap",
        freezeInfo && panelBackgroundStyles[headerColor],
        frozenCellClassName(freezeInfo),
        className,
      )}
      style={{ width: column.width, ...frozenCellStyle(freezeInfo) }}
      onClick={handleClick}
    >
      <div
        className="inline-flex items-center gap-1 whitespace-nowrap"
        style={
          freezeInfo
            ? undefined
            : {
                position: "sticky",
                left: frozen.leftReservedWidth + paddingX,
                right: frozen.rightReservedWidth + paddingX,
              }
        }
      >
        {column.renderHead ? (
          column.renderHead(column)
        ) : (
          <Typography.Span content={column.label} noWrap />
        )}
        {canSort && (
          <Icon
            path={sortDir === SortDirection.ASC ? mdiArrowUp : mdiArrowDown}
            size={16}
            className={cn("text-primary-500", !isSorted && "invisible")}
          />
        )}
      </div>
    </th>
  );
}
